import React from "react"

import { Skeleton } from "@coval/ui/skeleton"
import { Tooltip } from "@coval/ui/tooltip"
import { Copy, Trash2 } from "lucide-react"

import { handleAccessibleRowKeyDown } from "@/utils/accessibleRowKeyDown"
import { cn } from "@/utils/cn"

import { formatDate } from "../../utils/filterSimulations"
import { formatTraceValues, parseTraceValues } from "../recentMetricValues"
import type { ExperimentTestResult, RecentSimulationItem } from "../types"
import { ReviewBadge } from "./ReviewBadge"

export type TestResult = ExperimentTestResult

interface RecentSimulationRowProps {
  simulation: RecentSimulationItem
  testResult: TestResult | null
  isEvaluating: boolean
  isActive?: boolean
  onRowClick: () => void
  onPointerEnter?: () => void
  onPointerLeave?: () => void
  onDelete: () => void
  metricLabel?: string
  isTraceMetric?: boolean
  showTestResult?: boolean
}

export const RecentSimulationRow: React.FC<RecentSimulationRowProps> = ({
  simulation,
  testResult,
  isEvaluating,
  isActive = false,
  onRowClick,
  onPointerEnter,
  onPointerLeave,
  onDelete,
  metricLabel,
  isTraceMetric = false,
  showTestResult = true,
}) => {
  const showTraceValues =
    isTraceMetric &&
    (simulation.metricIsValues ||
      testResult?.rawMetricOutput?.output_type === "set")
  const traceValues = showTraceValues
    ? parseTraceValues(simulation.metricValue)
    : null
  const testTraceValues =
    isTraceMetric &&
    (!testResult?.errorStatus || testResult.errorStatus === "COMPLETED") &&
    testResult?.rawMetricOutput?.output_type === "set" &&
    Array.isArray(testResult.rawMetricOutput.set_value)
      ? testResult.rawMetricOutput.set_value
      : null
  const handleCopyId = (e: React.MouseEvent) => {
    e.stopPropagation()
    void navigator.clipboard.writeText(simulation.id)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete()
  }

  return (
    <tr
      className={cn(
        "border-b border-coval-border hover:bg-surface-1/50 cursor-pointer transition-colors group",
        isActive && "bg-surface-1/50",
      )}
      data-recent-simulation-id={simulation.id}
      tabIndex={0}
      onClick={onRowClick}
      onKeyDown={(event) => handleAccessibleRowKeyDown(event, onRowClick)}
      onMouseEnter={onPointerEnter}
      onMouseLeave={onPointerLeave}
    >
      <td className="px-4 py-4 text-left" style={{ verticalAlign: "middle" }}>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-coval-text-muted font-mono">
            {simulation.id.slice(0, 8)}
          </span>
          <Tooltip content="Copy ID">
            <button
              onClick={handleCopyId}
              aria-label="Copy ID"
              className="text-coval-text-muted hover:text-coval-text-muted transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
          {simulation.isImported && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-coval-contrast/10 text-coval-contrast">
              imported
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-4 text-left" style={{ verticalAlign: "top" }}>
        <span className="text-sm text-coval-text-muted">
          {formatDate(simulation.createdAt)}
        </span>
      </td>
      {metricLabel !== undefined && (
        <td
          className="px-4 py-4 text-left"
          style={{ verticalAlign: "top" }}
          data-dd-privacy={showTraceValues ? "mask" : undefined}
          data-ph-capture={showTraceValues ? "false" : undefined}
        >
          {simulation.metricValue != null && showTraceValues ? (
            <span
              data-dd-privacy="mask"
              data-ph-capture="false"
              className="text-xs text-coval-text-muted"
            >
              {traceValues
                ? formatTraceValues(traceValues)
                : String(simulation.metricValue)}
            </span>
          ) : simulation.metricValue != null ? (
            <ReviewBadge value={String(simulation.metricValue)} />
          ) : (
            <span className="text-xs text-coval-text-muted">—</span>
          )}
        </td>
      )}
      {showTestResult ? (
        <td
          className="px-4 py-4 text-left"
          style={{ verticalAlign: "top" }}
          data-dd-privacy={showTraceValues ? "mask" : undefined}
          data-ph-capture={showTraceValues ? "false" : undefined}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 items-center justify-center">
              {isEvaluating ? (
                <Skeleton className="w-full h-4 rounded-lg" />
              ) : testResult ? (
                <div className="flex items-start gap-2">
                  {testTraceValues ? (
                    <span className="text-xs text-coval-text-muted">
                      {formatTraceValues(testTraceValues)}
                    </span>
                  ) : (
                    <ReviewBadge
                      value={testResult.value}
                      errorStatus={testResult.errorStatus}
                      errorMessage={testResult.errorMessage}
                    />
                  )}
                  <span className="text-sm text-coval-text line-clamp-2">
                    {testResult.explanation}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-coval-text-muted">
                  Not tested
                </span>
              )}
            </div>
            <Tooltip content="Remove from list">
              <button
                onClick={handleDelete}
                aria-label="Remove from list"
                type="button"
                className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 text-coval-text-muted hover:text-coval-danger-text transition-all"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </Tooltip>
          </div>
        </td>
      ) : (
        <td
          className="px-4 py-4 text-right"
          style={{ verticalAlign: "middle" }}
        >
          <Tooltip content="Remove from list">
            <button
              type="button"
              onClick={handleDelete}
              aria-label="Remove from list"
              className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 text-coval-text-muted hover:text-coval-danger-text transition-all"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        </td>
      )}
    </tr>
  )
}
